import { reactive, computed, ref } from 'vue'
import { useChatSocket } from './useChatSocket'

/**
 * Message auto-translation layer.
 *
 * This composable is a singleton that keeps a reactive map of
 * `messageId -> translation state`. Components (`ChatMessageItem`) call
 * `onMessageArrival(msg)` once per mounted message; the composable takes
 * care of skip rules, cache, rate limiting, and rendering state.
 *
 * Batch hydration (`hydrateBatch`) is used on initial history load so
 * opening a 50-message thread sends at most 2 batch HTTP calls instead of
 * 50 single-message ones.
 *
 * Re-hydration (`clearAndRehydrate`) is triggered when the user switches
 * their preferred language (either locally via Save, or on another open tab
 * via the `settings.update` WS frame). Clearing the map makes every mounted
 * MessageItem watcher re-fire and re-request with the new target language.
 */

export type TranslationStatus = 'pending' | 'ok' | 'failed' | 'skipped'

export interface TranslationState {
    translatedText: string | null
    sourceLang: string | null
    sameLang: boolean
    showOriginal: boolean
    status: TranslationStatus
    errorCode?: string
}

interface ChatMessageLike {
    id: string
    body: string
    author?: { id?: string }
    deletedAt?: string | null
}

const MAX_INFLIGHT = 4
const BATCH_CHUNK_SIZE = 30
// ≥ 2 letter characters anywhere — skips emoji-only, pure numbers, and
// pure-URL bodies. \p{L} is the Unicode letter category (covers ж, 日, etc.).
const LETTER_RE = /\p{L}{2,}/u
const URL_ONLY_RE = /^\s*https?:\/\/\S+\s*$/i
// Custom-emoji shortcodes. Strip these before the letter-test so a message
// like `:party_parrot:` isn't sent to LibreTranslate as "party parrot" (the
// inner letters would otherwise satisfy LETTER_RE).
const EMOJI_TOKEN_RE = /:[a-zA-Z0-9_-]{1,48}:/g
const URL_RE = /https?:\/\/\S+/gi

const state = reactive<Map<string, TranslationState>>(new Map())
const inFlightCount = ref(0)
const queue: Array<() => Promise<void>> = []
let socketWired = false

function isOwnMessage(msg: ChatMessageLike, currentUserId: string | null): boolean {
    return !!currentUserId && String(msg.author?.id || '') === currentUserId
}

function shouldSkip(msg: ChatMessageLike, target: string | null, currentUserId: string | null): boolean {
    if (!target) return true
    if (!msg || !msg.id) return true
    if (msg.deletedAt) return true
    if (isOwnMessage(msg, currentUserId)) return true
    const body = (msg.body || '').trim()
    if (!body) return true
    if (URL_ONLY_RE.test(body)) return true
    // Strip shortcodes + URLs so only actual prose is tested for letters.
    const prose = body.replace(EMOJI_TOKEN_RE, ' ').replace(URL_RE, ' ').trim()
    if (!prose) return true
    if (!LETTER_RE.test(prose)) return true
    return false
}

function pump() {
    while (inFlightCount.value < MAX_INFLIGHT && queue.length > 0) {
        const job = queue.shift()
        if (!job) break
        inFlightCount.value++
        job().finally(() => {
            inFlightCount.value--
            // Re-pump in case new jobs queued while this one was in flight
            pump()
        })
    }
}

function enqueue(job: () => Promise<void>) {
    queue.push(job)
    pump()
}

async function fetchSingle(msg: ChatMessageLike, target: string): Promise<void> {
    const current = state.get(msg.id)
    // Skip re-fetch if already resolved or actively pending
    if (current && (current.status === 'ok' || current.status === 'pending')) return
    state.set(msg.id, {
        translatedText: null,
        sourceLang: null,
        sameLang: false,
        showOriginal: false,
        status: 'pending'
    })
    try {
        const res = await $fetch<any>('/api/chat/translate', {
            method: 'POST',
            body: { messageId: msg.id, text: msg.body, target }
        })
        if (res?.ok === false) {
            state.set(msg.id, {
                translatedText: null,
                sourceLang: null,
                sameLang: false,
                showOriginal: false,
                status: 'failed',
                errorCode: res.code
            })
            return
        }
        state.set(msg.id, {
            translatedText: res.translatedText ?? null,
            sourceLang: res.sourceLang ?? null,
            sameLang: !!res.sameLang,
            showOriginal: false,
            status: 'ok'
        })
    } catch (err: any) {
        // 429 etc. — $fetch throws on non-2xx even when we returned structured
        // JSON. Peel the payload out if present.
        const code = err?.data?.code || 'translate_failed'
        state.set(msg.id, {
            translatedText: null,
            sourceLang: null,
            sameLang: false,
            showOriginal: false,
            status: 'failed',
            errorCode: code
        })
    }
}

function wireSocket() {
    if (socketWired) return
    socketWired = true
    const { on } = useChatSocket()
    on('settings.update', () => {
        clearAndRehydrate()
    })
}

/**
 * Called by ChatMessageItem on mount/update. Applies skip rules and kicks
 * off a single-message translation if we don't already have a state.
 *
 * Once a message has any state (pending/ok/failed/skipped), we never
 * auto-retry — the dedicated retry button (`retryTranslation`) is the
 * only path to re-fetch. The previous behaviour re-enqueued on
 * `status === 'failed'`, which combined with the array-getter watcher
 * in ChatMessageItem (each getter run returns a new array ref → cb
 * fires on every state mutation) produced an endless 429 loop when the
 * rate limiter denied the first call.
 */
export function onMessageArrival(
    msg: ChatMessageLike,
    target: string | null,
    currentUserId: string | null
): void {
    wireSocket()
    if (shouldSkip(msg, target, currentUserId)) return
    if (state.has(msg.id)) return
    enqueue(() => fetchSingle(msg, target!))
}

/**
 * Bulk-hydrate the state map from an initial history page. Safe to call
 * multiple times — already-known messages are filtered out before batching.
 */
export async function hydrateBatch(
    messages: ChatMessageLike[],
    target: string | null,
    currentUserId: string | null
): Promise<void> {
    wireSocket()
    if (!target) return

    const toTranslate = messages.filter(
        m => !shouldSkip(m, target, currentUserId) && !state.has(m.id)
    )
    if (toTranslate.length === 0) return

    // Mark everything as pending so MessageItem doesn't re-enqueue them
    for (const m of toTranslate) {
        state.set(m.id, {
            translatedText: null,
            sourceLang: null,
            sameLang: false,
            showOriginal: false,
            status: 'pending'
        })
    }

    // Fire chunks in parallel (each is 1 RL token on the server side)
    const chunks: ChatMessageLike[][] = []
    for (let i = 0; i < toTranslate.length; i += BATCH_CHUNK_SIZE) {
        chunks.push(toTranslate.slice(i, i + BATCH_CHUNK_SIZE))
    }

    await Promise.all(chunks.map(async (chunk) => {
        try {
            const res = await $fetch<any>('/api/chat/translate/batch', {
                method: 'POST',
                body: {
                    target,
                    items: chunk.map(m => ({ messageId: m.id, text: m.body }))
                }
            })
            for (const r of (res?.results || [])) {
                if (!r?.messageId) continue
                if (r.ok === false) {
                    state.set(r.messageId, {
                        translatedText: null,
                        sourceLang: null,
                        sameLang: false,
                        showOriginal: false,
                        status: 'failed',
                        errorCode: r.code
                    })
                } else {
                    state.set(r.messageId, {
                        translatedText: r.translatedText ?? null,
                        sourceLang: r.sourceLang ?? null,
                        sameLang: !!r.sameLang,
                        showOriginal: false,
                        status: 'ok'
                    })
                }
            }
        } catch (err: any) {
            const code = err?.data?.code || 'translate_failed'
            for (const m of chunk) {
                state.set(m.id, {
                    translatedText: null,
                    sourceLang: null,
                    sameLang: false,
                    showOriginal: false,
                    status: 'failed',
                    errorCode: code
                })
            }
        }
    }))
}

export function toggleShowOriginal(messageId: string): void {
    const s = state.get(messageId)
    if (!s) return
    s.showOriginal = !s.showOriginal
}

export function retryTranslation(
    msg: ChatMessageLike,
    target: string | null,
    currentUserId: string | null
): void {
    if (shouldSkip(msg, target, currentUserId)) return
    state.delete(msg.id)
    enqueue(() => fetchSingle(msg, target!))
}

/**
 * Wipe every cached translation. Callers include:
 *  - User saves a new preferred language (local tab)
 *  - WS `settings.update` frame from another tab of the same user
 *
 * MessageItem watchers re-fire on the next tick because they depend on
 * `getState(msg.id)`, which switches from a populated reactive entry to
 * undefined and back to pending via `onMessageArrival`.
 */
export function clearAndRehydrate(): void {
    state.clear()
    // Queue is safe to leave — in-flight fetches will still complete and
    // populate state, but against the old (now-stale) target. We accept that
    // brief window; MessageItem watchers re-fire and overwrite them anyway.
}

export function getState(messageId: string): TranslationState | undefined {
    return state.get(messageId)
}

export function useChatTranslation() {
    return {
        onMessageArrival,
        hydrateBatch,
        toggleShowOriginal,
        retryTranslation,
        clearAndRehydrate,
        getState,
        inFlightCount: computed(() => inFlightCount.value)
    }
}
